'use client';

import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import Image from 'next/image';
import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useIsMounted, useToggle } from 'usehooks-ts';
import { lato } from '@provider-directory/app/ui/fonts';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { getDeploymentEnvironment } from '@provider-directory/services/getEnvironmentVariables';
import {
  getHeaderNavigationItems,
  type WhiteplainsMenuItem,
  ParentWhiteplainsMenuItem,
} from './header-data';
import { BASE_URL, WPBaseLink } from './whiteplains-utils';

import './whiteplains-header.css';

interface DropdownParentProps {
  children: React.ReactNode;
}

interface DropdownParentMobileProps extends DropdownParentProps {
  dropdownId: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
}

const DropdownParent = ({ children }: DropdownParentProps): ReactElement => {
  const [hovered, setHovered] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);
  const isOpen = hovered || keyboardOpen;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        // Only toggle when the event target is the direct trigger (button/link), not a child link
        const target = e.target as HTMLElement;
        const isDirectTrigger =
          target.classList.contains('js-dropdown-toggle') ||
          target.closest('.js-dropdown-toggle') !== null;
        if (!isDirectTrigger) {
          return;
        }

        e.preventDefault();
        setKeyboardOpen((prev) => {
          const opening = !prev;
          if (opening) {
            // Move focus to the first focusable element inside the dropdown
            requestAnimationFrame(() => {
              const dropdown = containerRef.current?.querySelector(
                '.c-nav-primary__mega-container, ul ul',
              );
              const firstFocusable = dropdown?.querySelector<HTMLElement>(
                'a, button, [tabindex]:not([tabindex="-1"])',
              );
              firstFocusable?.focus();
            });
          }
          return opening;
        });
      } else if (e.key === 'Escape' && keyboardOpen) {
        e.preventDefault();
        setKeyboardOpen(false);
        // Return focus to the trigger button
        const trigger = containerRef.current?.querySelector<HTMLElement>(
          '.js-dropdown-toggle',
        );
        trigger?.focus();
      }
    },
    [keyboardOpen],
  );

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Close the dropdown when focus moves outside the container
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setKeyboardOpen(false);
    }
  }, []);

  // Keep aria-expanded in sync with the open state on the trigger element
  useEffect(() => {
    const trigger = containerRef.current?.querySelector<HTMLElement>(
      '.js-dropdown-toggle',
    );
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(isOpen));
    }
  }, [isOpen]);

  return (
    <li
      ref={containerRef}
      className={`js-dropdown-parent ${isOpen ? 'over' : ''}`}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      {children}
    </li>
  );
};

const DropdownParentMobile = ({
  children,
  dropdownId,
  isOpen,
  onToggle,
}: DropdownParentMobileProps): ReactElement => {
  const toggleDropdown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle(dropdownId);
    },
    [dropdownId, onToggle],
  );

  return (
    <li
      className={`js-dropdown-parent ${isOpen ? 'over' : ''}`}
      onClick={toggleDropdown}
    >
      {children}
    </li>
  );
};

const WphDesktopMenuItem = ({
  children,
  isRoot,
  text,
  href,
  className,
  isExternal,
  isInternal,
  ...rest
}: WhiteplainsMenuItem & { isRoot?: boolean }): ReactElement => {
  const analytics = useAnalytics();
  const handleClick = useCallback(() => {
    if (href !== 'javascript:;') {
      analytics.navigationClickEvent({
        clickText: text,
        linkType: 'link',
        navigationType: 'top-bar',
      });
    }
  }, [analytics, text, href]);
  if (children && children.length > 0) {
    if (isRoot) {
      const { ariaControls, label, id } = rest as ParentWhiteplainsMenuItem;
      return (
        <DropdownParent key={text}>
          <WPBaseLink
            className={clsx(className, 'parent js-dropdown-toggle')}
            data-is-dropdown={'true'}
            aria-haspopup={'true'}
            href={href}
            data-aria-controls={`"${ariaControls}"`}
            id={id}
            aria-controls={ariaControls}
            aria-label={label}
            onClick={handleClick}
          >
            {text}
          </WPBaseLink>
          {text !== 'Services' ? (
            <div
              className={'c-nav-primary__mega-container js-dropdown'}
              id={ariaControls}
              aria-labelledby={id}
              role={'menu'}
            >
              <ul role={'none'}>
                {children.map((child) => (
                  <WphDesktopMenuItem
                    key={child.text}
                    {...child}
                    isRoot={false}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </DropdownParent>
      );
    }
    return (
      <DropdownParent>
        <WPBaseLink
          key={text}
          href={href}
          className={'parent js-dropdown-toggle'}
          onClick={handleClick}
        >
          {text}
        </WPBaseLink>
        <ul>
          <li className={'js-cloned-nav-item'} style={{ display: 'none' }}>
            <WPBaseLink href={href} className={'parent'} onClick={handleClick}>
              {text}
            </WPBaseLink>
          </li>
          {children.map((child) => (
            <WphDesktopMenuItem key={child.text} {...child} isRoot={false} />
          ))}
        </ul>
      </DropdownParent>
    );
  }

  return (
    <li>
      <WPBaseLink
        href={href}
        key={text}
        isInternal={isInternal}
        onClick={handleClick}
        {...(isExternal ? { target: '_blank', rel: 'noreferrer' } : {})}
      >
        {text}
      </WPBaseLink>
    </li>
  );
};

const WphMobileMenuItem = ({
  children,
  isRoot,
  text,
  href,
  isExternal,
  isInternal,
  className,
  onClick,
  dropdownId,
  isDropdownOpen,
  onDropdownToggle,
  ...rest
}: WhiteplainsMenuItem & {
  isRoot?: boolean;
  onClick?: () => void;
  dropdownId?: string;
  isDropdownOpen?: boolean;
  onDropdownToggle?: (id: string) => void;
}): ReactElement => {
  const analytics = useAnalytics();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent) => {
      // prevents the dropdown from closing for any link click
      e.stopPropagation();
      // Only handle navigation for real links, not dropdown toggles
      if (href !== 'javascript:;') {
        analytics.navigationClickEvent({
          clickText: text,
          linkType: 'link',
          navigationType: 'top-bar',
        });

        if (isInternal && href) {
          onClick?.();
        }
      } else {
        // For dropdown toggles, prevent default navigation
        e.preventDefault();
      }
    },
    [analytics, text, href, onClick, isInternal],
  );

  if (children && children.length > 0) {
    const { ariaControls, label, id } = rest as ParentWhiteplainsMenuItem;
    if (isRoot) {
      return (
        <DropdownParentMobile
          dropdownId={id}
          isOpen={isDropdownOpen ?? false}
          onToggle={onDropdownToggle ?? (() => undefined)}
        >
          <WPBaseLink
            className={className ?? 'parent js-dropdown-toggle'}
            data-is-dropdown={'true'}
            aria-haspopup={'true'}
            href={href}
            data-aria-controls={`"${ariaControls}"`}
            id={id}
            aria-controls={ariaControls}
            aria-label={label}
            onClick={handleLinkClick}
          >
            {text}
          </WPBaseLink>
          <MaterialSymbol
            name={'keyboard_arrow_down'}
            className={'chevron'}
            color={'#71b2c9'}
            style={{ color: '#71b2c9' }}
          />
          <ul id={ariaControls} aria-labelledby={id}>
            {children.map((child) => (
              <WphMobileMenuItem
                key={child.text}
                {...child}
                isRoot={false}
                onClick={onClick}
                dropdownId={dropdownId}
                isDropdownOpen={isDropdownOpen}
                onDropdownToggle={onDropdownToggle}
              />
            ))}
          </ul>
        </DropdownParentMobile>
      );
    }
    return (
      <li className={'js-dropdown-parent'}>
        <WPBaseLink
          className={'parent js-dropdown-toggle'}
          href={href}
          data-aria-controls={`"${ariaControls}"`}
          onClick={handleLinkClick}
        >
          {text}
        </WPBaseLink>
        <ul>
          <li className={'js-cloned-nav-item'} style={{ display: 'none' }}>
            <WPBaseLink href={href} className={'parent'}>
              {text}
            </WPBaseLink>
          </li>
          {children.map((child) => (
            <WphMobileMenuItem
              key={child.text}
              {...child}
              isRoot={false}
              onClick={onClick}
              dropdownId={dropdownId}
              isDropdownOpen={isDropdownOpen}
              onDropdownToggle={onDropdownToggle}
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <WPBaseLink
        href={href}
        onClick={handleLinkClick}
        {...(isExternal ? { target: '_blank', rel: 'noreferrer' } : {})}
        isInternal={isInternal}
      >
        {text}
      </WPBaseLink>
    </li>
  );
};

interface NavigationProps {
  navigationItems: WhiteplainsMenuItem[];
}

interface MobileNavigationProps extends NavigationProps {
  onNavClose: () => void;
  navOpen: boolean;
}

const DesktopNav: FC<NavigationProps> = ({ navigationItems }): ReactElement => (
  <nav
    className={'c-nav-primary js-nav-primary is-desktop'}
    aria-label={'Main navigation'}
  >
    <ul>
      {navigationItems.map((item) => (
        <WphDesktopMenuItem key={item.text} {...item} isRoot={true} />
      ))}
    </ul>
  </nav>
);

const MobileNav: FC<MobileNavigationProps> = ({
  navigationItems,
  onNavClose,
  navOpen,
}): ReactElement => {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const handleDropdownToggle = useCallback((dropdownId: string) => {
    setOpenDropdownId((prevId) => (prevId === dropdownId ? null : dropdownId));
  }, []);

  useLayoutEffect(() => {
    if (navOpen) {
      document.body.classList.add('is-dropdown-open');
    }
    return () => {
      document.body.classList.remove('is-dropdown-open');
    };
  }, [navOpen]);

  useLayoutEffect(() => {
    if (!navOpen) {
      setOpenDropdownId(null);
    }
  }, [navOpen]);

  return (
    <nav
      className={'c-nav-primary js-nav-primary is-mobile'}
      aria-label={'Main navigation'}
    >
      <ul>
        {navigationItems.map((item) => (
          <WphMobileMenuItem
            key={item.text}
            {...item}
            isRoot={true}
            onClick={onNavClose}
            isDropdownOpen={
              openDropdownId === (item as ParentWhiteplainsMenuItem).id
            }
            onDropdownToggle={handleDropdownToggle}
          />
        ))}
      </ul>
    </nav>
  );
};

const Search = (): ReactElement => {
  const analytics = useAnalytics();
  return (
    <div className={'c-header-primary__searchicon'}>
      <WPBaseLink
        href={'/search'}
        className={'js-header-primary-toggle-search'}
        onClick={() => {
          analytics.navigationClickEvent({
            clickText: 'Search',
            linkType: 'link',
            navigationType: 'top-bar',
          });
        }}
      >
        <span className={'small sr-only'}>
          Toggle
          <br />
          search
        </span>
        <MaterialSymbol
          name={'search'}
          aria-hidden={true}
          className={'fa is-icon-search'}
          size={28}
        />
        <MaterialSymbol
          name={'close'}
          style={{ color: '#00549f' }}
          aria-hidden={true}
          className={'fa is-icon-close'}
          size={32}
        />
      </WPBaseLink>
    </div>
  );
};

export const WhiteplainsHeader = (): ReactElement => {
  const navigationItems = getHeaderNavigationItems(getDeploymentEnvironment());
  const [navOpen, toggleNav] = useToggle(false);
  const [navHeight, setNavHeight] = useState('calc(100dvh - 170px)');
  const analytics = useAnalytics();
  const { t } = useTranslation('montefiore_wph');
  const isMobileNav = useMediaQuery({ maxWidth: 992 });
  const { featureFlags, featureFlagPayloads } = useFeatureFlags();
  const isMountedFn = useIsMounted();
  const isMounted = isMountedFn();

  useLayoutEffect(() => {
    const calculateNavHeight = () => {
      const topBanner = document.querySelector('[data-testid="top-banner"]');
      const localizationBanner = document.querySelector(
        '[data-testid="localization-selector-menu"]',
      );
      const baseHeight = 142;
      let bannerHeight = 0;
      if (topBanner && topBanner instanceof HTMLElement) {
        bannerHeight = topBanner.offsetHeight;
      }
      if (localizationBanner && localizationBanner instanceof HTMLElement) {
        bannerHeight += localizationBanner.offsetHeight;
      }
      setNavHeight(`calc(100dvh - ${baseHeight + bannerHeight}px)`);
    };
    calculateNavHeight();
    window.addEventListener('resize', calculateNavHeight);
    return () => {
      window.removeEventListener('resize', calculateNavHeight);
    };
  }, [
    featureFlags.isTopBannerEnabled,
    featureFlagPayloads.isTopBannerEnabled,
    featureFlags.isLocalizationEnabled,
    featureFlagPayloads.isLocalizationEnabled,
  ]);

  useLayoutEffect(() => {
    if (isMounted && !isMobileNav && navOpen) {
      toggleNav();
    }
  }, [isMounted, isMobileNav, navOpen, toggleNav]);

  // Close mobile nav on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && navOpen) {
        toggleNav();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navOpen, toggleNav]);

  return (
    <header
      className={clsx(
        lato.className,
        'c-header-primary js-header-primary is-fixed headroom headroom--top headroom--not-bottom',
      )}
    >
      <div className={'c-alert-banner'}>
        <div className={'c-alert-banner__content'}>
          <button className={'js-close-alert'}>
            <span className={'sr-only'}>Close alert banner</span>
            <span className={'fa fa-times'} aria-hidden={'true'} />
          </button>
          <p>
            <strong>
              Please note: Effective March 11, the Davis Avenue Parking Garage
              will be closed
            </strong>
            .
          </p>
          <p>
            Visit our{' '}
            <WPBaseLink
              href={`${BASE_URL}/patients-visitors/visitors/visitor-information/`}
              title={'Visitor Information page'}
              onClick={() => {
                analytics.navigationClickEvent({
                  clickText: 'Visitor Information page',
                  linkType: 'link',
                  navigationType: 'top-bar',
                });
              }}
            >
              <strong>Visitor Information page</strong>
            </WPBaseLink>{' '}
            for details.
          </p>
        </div>
      </div>
      <div className={'c-header-primary__top'}>
        <div
          className={`c-header-primary__container ${
            navOpen && 'is-mobile-nav-active'
          }`}
        >
          <div className={'c-header-primary__logo'}>
            <WPBaseLink
              href={'/'}
              onClick={() => {
                analytics.navigationClickEvent({
                  clickText: 'Navigate home',
                  linkType: 'link',
                  navigationType: 'top-bar',
                });
              }}
            >
              <Image
                src={`${BASE_URL}/img/logo.svg`}
                width={'210'}
                height={'95'}
                alt={t('aria.logoAlt', { orgName: 'White Plains Hospital' })}
                priority={true}
              />
            </WPBaseLink>
          </div>
          <div className={'c-header-primary__navicon'}>
            <button
              type={'button'}
              className={'js-header-primary-toggle-nav'}
              onClick={toggleNav}
              aria-label={
                navOpen
                  ? t('aria.closeNavigationMenu')
                  : t('aria.openNavigationMenu')
              }
              aria-expanded={navOpen}
            >
              <MaterialSymbol
                name={'menu'}
                style={{ color: '#00549f' }}
                aria-hidden={true}
                className={'is-icon-menu'}
                size={32}
              />
              <MaterialSymbol
                name={'close'}
                style={{ color: '#00549f' }}
                aria-hidden={true}
                className={'is-icon-close'}
                size={32}
              />
            </button>
          </div>
          <div
            className={'c-header-primary__nav'}
            style={
              navOpen && isMounted && isMobileNav
                ? { height: navHeight }
                : undefined
            }
          >
            <DesktopNav navigationItems={navigationItems} />
            <MobileNav
              navOpen={navOpen}
              navigationItems={navigationItems}
              onNavClose={toggleNav}
            />
          </div>
          <Search />
        </div>
      </div>
    </header>
  );
};
